import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";

const programs = [
  {
    title: "Junior Penetration Tester",
    route: "/junior-penetration-tester",
    level: "Entry Level",
    description: "AppSec, VAPT, OWASP, Network Security, Web Security, CTFs",
    icon: "🛡️",
    badge: "Beginner Friendly",
  },
  {
    title: "Senior Penetration Tester",
    route: "/senior-penetration-tester",
    level: "Advanced Level",
    description:
      "Advanced VAPT, Mobile, API, Thick Client Security, Exploitation, Reporting",
    icon: "⚔️",
    badge: "Advanced Track",
  },
];

function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktopProgramsOpen, setDesktopProgramsOpen] = useState(false);
  const [mobileProgramsOpen, setMobileProgramsOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const desktopProgramsRef = useRef(null);

  const closeAllMenus = () => {
    setMobileMenuOpen(false);
    setDesktopProgramsOpen(false);
    setMobileProgramsOpen(false);
  };

  const handleApplyNowClick = () => {
    closeAllMenus();

    if (
      location.pathname === "/junior-penetration-tester" ||
      location.pathname === "/senior-penetration-tester"
    ) {
      setTimeout(() => {
        document.getElementById("demoRegistration")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 350);
    } else {
      navigate("/junior-penetration-tester#demoRegistration");
    }
  };

  useEffect(() => {
    function handleDesktopOutsideClick(event) {
      if (
        desktopProgramsRef.current &&
        !desktopProgramsRef.current.contains(event.target)
      ) {
        setDesktopProgramsOpen(false);
      }
    }

    function handleEscape(event) {
      if (event.key === "Escape") {
        closeAllMenus();
      }
    }

    document.addEventListener("click", handleDesktopOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("click", handleDesktopOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    closeAllMenus();
  }, [location.pathname]);

  const navLinkClass = ({ isActive }) =>
    `relative px-1 py-2 text-[15px] font-black tracking-wide transition-all duration-300
    after:absolute after:left-0 after:-bottom-1 after:h-[3px] after:rounded-full after:bg-gradient-to-r after:from-cyan-400 after:via-blue-500 after:to-purple-500 after:transition-all after:duration-300 after:content-['']
    ${
      isActive
        ? "text-blue-950 after:w-full"
        : "text-slate-700 hover:text-blue-950 after:w-0 hover:after:w-full"
    }`;

  const mobileLinkClass =
    "block w-full px-5 py-4 text-base font-black text-left transition rounded-2xl text-slate-800 hover:bg-blue-50 hover:text-blue-800";

 const applyButtonClass =
  "group relative isolate inline-flex items-center justify-center overflow-hidden rounded-md px-8 py-3.5 text-base font-black text-white transition-all duration-300 hover:scale-105 active:scale-95 " +
  "bg-gradient-to-r from-slate-950 via-blue-950 to-slate-950 " +
  "shadow-[0_0_35px_rgba(14,165,233,0.75),0_0_70px_rgba(37,99,235,0.55),0_0_110px_rgba(124,58,237,0.45)] " +
  "hover:shadow-[0_0_45px_rgba(34,211,238,1),0_0_95px_rgba(59,130,246,0.95),0_0_145px_rgba(168,85,247,0.85)] " +
  "before:absolute before:-inset-2 before:-z-10 before:rounded-md before:bg-gradient-to-r before:from-cyan-300 before:via-blue-500 before:to-purple-500 before:blur-2xl before:opacity-90 before:animate-pulse " +
  "after:absolute after:inset-0 after:-z-10 after:rounded-md after:bg-gradient-to-r after:from-slate-950 after:via-blue-950 after:to-slate-950 " +
  "ring-2 ring-cyan-300/40 hover:ring-cyan-300/80";
  
  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="px-3 py-4 mx-auto max-w-7xl sm:px-5 lg:px-8">
        <nav className="relative flex items-center justify-between overflow-visible rounded-md border border-white/60 bg-white/70 px-4 py-2 shadow-[0_25px_90px_rgba(15,23,42,0.16)] backdrop-blur-2xl ring-1 ring-slate-900/5 xl:px-6">
          <div className="absolute inset-0 rounded-md bg-gradient-to-r from-white/60 via-blue-50/40 to-white/60"></div>

          {/* LOGO */}
          <Link
            to="/"
            onClick={closeAllMenus}
            className="relative z-10 flex items-center"
          >
            <img
              src={logo}
              alt="Practical Infosec"
              className="object-contain w-auto transition duration-300 h-14 hover:scale-105"
            />
          </Link>
 
          {/* DESKTOP MENU */}
          <div className="relative z-10 items-center hidden gap-10 xl:flex">
            <NavLink to="/" className={navLinkClass}>
              Home
            </NavLink>

            {/* PROGRAMS DROPDOWN */}
            <div className="relative" ref={desktopProgramsRef}>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setDesktopProgramsOpen((prev) => !prev);
                }}
                className={`relative flex items-center gap-2 px-1 py-2 text-[15px] font-black tracking-wide transition-all duration-300
                after:absolute after:left-0 after:-bottom-1 after:h-[3px] after:rounded-full after:bg-gradient-to-r after:from-cyan-400 after:via-blue-500 after:to-purple-500 after:transition-all after:duration-300 after:content-['']
                ${
                  desktopProgramsOpen
                    ? "text-blue-950 after:w-full"
                    : "text-slate-700 hover:text-blue-950 after:w-0 hover:after:w-full"
                }`}
              >
                Programs
                <span
                  className={`text-xs transition-transform duration-300 ${
                    desktopProgramsOpen ? "rotate-180" : ""
                  }`}
                >
                  ▼
                </span>
              </button>

              {desktopProgramsOpen && (
                <div
                  onClick={(event) => event.stopPropagation()}
                  className="absolute left-1/2 top-14 w-[440px] -translate-x-1/2 overflow-hidden rounded-[2rem] border border-white/70 bg-white/95 p-4 shadow-[0_35px_100px_rgba(15,23,42,0.22)] backdrop-blur-2xl ring-1 ring-slate-900/5"
                >
                  <div className="relative p-5 overflow-hidden rounded-3xl bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,165,233,0.25),transparent_35%)]"></div>

                    <div className="relative">
                      <p className="text-xs font-black tracking-widest uppercase text-cyan-300">
                        Live Cybersecurity Programs
                      </p>

                      <h3 className="mt-2 text-xl font-black text-white">
                        Choose Your Training Path
                      </h3>

                      <p className="mt-2 text-sm font-semibold leading-6 text-slate-300">
                        Practical AppSec, VAPT, API security, mobile security,
                        reporting, and career-focused training.
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 space-y-3">
                    {programs.map((program) => (
                      <Link
                        key={program.route}
                        to={program.route}
                        onClick={closeAllMenus}
                        className="block p-4 transition-all duration-300 bg-white border group rounded-3xl border-slate-200 hover:-translate-y-1 hover:border-blue-300 hover:bg-blue-50/80 hover:shadow-xl"
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 text-2xl transition rounded-2xl bg-gradient-to-br from-blue-700 to-cyan-500 group-hover:scale-110">
                            {program.icon}
                          </div>

                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-xs font-black tracking-widest text-blue-700 uppercase">
                                  {program.level}
                                </p>

                                <h4 className="mt-1 text-base font-black text-slate-950">
                                  {program.title}
                                </h4>
                              </div>

                              <span className="px-3 py-1 text-[11px] font-black text-blue-700 bg-blue-100 rounded-full whitespace-nowrap">
                                {program.badge}
                              </span>
                            </div>

                            <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">
                              {program.description}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <NavLink to="/blog" className={navLinkClass}>
              Blog
            </NavLink>

            <NavLink to="/about" className={navLinkClass}>
              About
            </NavLink>

            <NavLink to="/contact-us" className={navLinkClass}>
              Contact Us
            </NavLink>
          </div>

          {/* DESKTOP ACTION */}
          <div className="relative z-10 items-center hidden gap-4 xl:flex">
            <button
              type="button"
              onClick={handleApplyNowClick}
              className={applyButtonClass}
            >
              <span className="relative z-10 flex items-center gap-2">
                Apply Now
                <span className="transition-transform duration-300 group-hover:translate-x-1">
                  →
                </span>
              </span>
            </button>
          </div>

          {/* MOBILE MENU BUTTON */}
          <button
            type="button"
            onClick={() => {
              setMobileMenuOpen((prev) => !prev);
              setMobileProgramsOpen(false);
              setDesktopProgramsOpen(false);
            }}
            className="relative z-10 flex items-center justify-center w-12 h-12 text-2xl font-black transition border rounded-full xl:hidden border-white/60 bg-white/80 text-slate-900 backdrop-blur-xl hover:bg-blue-50"
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? "×" : "☰"}
          </button>
        </nav>

        {/* MOBILE MENU */}
        {mobileMenuOpen && (
          <div className="mt-4 overflow-hidden border shadow-2xl xl:hidden rounded-[2rem] border-white/60 bg-white/95 p-4 backdrop-blur-2xl ring-1 ring-slate-900/5">
            <div className="p-4 mb-4 rounded-3xl bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950">
              <p className="text-xs font-black tracking-widest uppercase text-cyan-300">
                Practical Infosec
              </p>

              <h3 className="mt-2 text-xl font-black text-white">
                Cybersecurity Training Platform
              </h3>
            </div>

            <div className="space-y-2">
              <NavLink to="/" onClick={closeAllMenus} className={mobileLinkClass}>
                Home
              </NavLink>

              <div>
                <button
                  type="button"
                  onClick={() => setMobileProgramsOpen((prev) => !prev)}
                  className="flex items-center justify-between w-full px-5 py-4 text-base font-black transition rounded-2xl text-slate-800 hover:bg-blue-50 hover:text-blue-800"
                >
                  Programs
                  <span className="text-xl">
                    {mobileProgramsOpen ? "−" : "+"}
                  </span>
                </button>

                {mobileProgramsOpen && (
                  <div className="p-3 mt-3 space-y-3 border rounded-3xl bg-slate-50 border-slate-200">
                    {programs.map((program) => (
                      <NavLink
                        key={program.route}
                        to={program.route}
                        onClick={closeAllMenus}
                        className="block p-4 transition bg-white border rounded-2xl border-slate-200 hover:border-blue-300 hover:bg-blue-50"
                      >
                        <p className="text-xs font-black tracking-widest text-blue-700 uppercase">
                          {program.level}
                        </p>

                        <p className="mt-1 text-base font-black text-slate-950">
                          {program.title}
                        </p>

                        <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">
                          {program.description}
                        </p>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>

              <NavLink
                to="/blog"
                onClick={closeAllMenus}
                className={mobileLinkClass}
              >
                Blog
              </NavLink>

              <NavLink
                to="/about"
                onClick={closeAllMenus}
                className={mobileLinkClass}
              >
                About
              </NavLink>

              <NavLink
                to="/contact-us"
                onClick={closeAllMenus}
                className={mobileLinkClass}
              >
                Contact Us
              </NavLink>

              <button
                type="button"
                onClick={handleApplyNowClick}
                className={`${applyButtonClass} mt-4 w-full`}
              >
                <span className="relative z-10">Apply Now →</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

export default Navbar;